import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import Sidebar from './components/sidebar/Sidebar';
import NavbarAdmin from './components/navbar/NavbarAdmin';
import { Card, CardContent } from './components/ui/card';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { HiUserGroup, HiMailOpen, HiOutlineLightningBolt } from 'react-icons/hi';
import { getAdminUserStats, getOrderStats, getReportStats , getEmailStats } from './services/adminService';

const COLORS = ['#C77DAA', '#A6538A', '#FFBB28', '#FF8042', '#82ca9d', '#8884d8', '#00C49F'];

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [userBehaviorData, setUserBehaviorData] = useState([]);
  const [orderStatsData, setOrderStatsData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [reportStats, setReportStats] = useState(null);
  const [userChartFilter, setUserChartFilter] = useState(localStorage.getItem('userChartFilter') || 'day');
  const [orderChartFilter, setOrderChartFilter] = useState(localStorage.getItem('orderChartFilter') || 'day');
  const { signOutUser, user, isLoading: authLoading, tokenReady } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [emailStats, setEmailStats] = useState(null);


  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = "/assets/favicon.ico";
    if (!tokenReady || authLoading) return;
    if (!user || user.role !== 'admin') {
      signOutUser();
    } else if (user.dashboardUrl !== slug) {
      navigate(`/admin/${user.dashboardUrl}`);
    } else {
      fetchStats();
      const interval = setInterval(fetchStats, 5000);
      return () => clearInterval(interval);
    }
  }, [user, slug, authLoading, tokenReady, userChartFilter, orderChartFilter]);

  useEffect(() => {
    localStorage.setItem('userChartFilter', userChartFilter);
  }, [userChartFilter]);

  useEffect(() => {
    localStorage.setItem('orderChartFilter', orderChartFilter);
  }, [orderChartFilter]);

const fetchStats = async () => {
  try {
    const [userRes, orderRes, reportRes, emailRes] = await Promise.all([
      getAdminUserStats(),
      getOrderStats(),
      getReportStats(),
      getEmailStats(), 
    ]);

    const usersCount = userRes.data.totalClients;
    const emailCount = emailRes.data.totalSent; 
    const confirmedOrders = orderRes.data.totalConfirmed;
    const pendingOrders = orderRes.data.totalPending;
    const canceledOrders = orderRes.data.totalCanceled;

    const formattedUsers = Object.entries(userRes.data[getKey(userChartFilter)] || {}).map(([date, value]) => ({ date, value }));
    const formattedOrders = Object.entries(orderRes.data[getKey(orderChartFilter)] || {}).map(([date, count]) => ({ date, count }));

    const top7Products = Object.entries(orderRes.data.topProducts || {})
      .map(([product, count]) => ({ name: product, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);

    setCards([
      {
        label: 'Utilisateurs',
        value: usersCount,
        icon: <HiUserGroup className="text-4xl text-white" />,
        bg: 'bg-gradient-to-br from-indigo-400 to-purple-500',
      },
      {
        label: 'Emails envoyés',
        value: emailCount,
        icon: <HiMailOpen className="text-4xl text-white" />,
        bg: 'bg-gradient-to-br from-pink-400 to-red-400',
      },
      {
        label: "Activité Commandes",
        value: `✔️ ${confirmedOrders} / ⏳ ${pendingOrders} / ❌ ${canceledOrders}`,
        icon: <HiOutlineLightningBolt className="text-4xl text-white" />,
        bg: 'bg-gradient-to-br from-yellow-400 to-orange-400',
      },
    ]);

    setUserBehaviorData(formattedUsers);
    setOrderStatsData(formattedOrders);
    setTopProducts(top7Products);
    setReportStats(reportRes.data);
    setEmailStats(emailRes.data); 
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};


  const getKey = (filter) => {
    return filter === 'day' ? 'byDay' : filter === 'month' ? 'byMonth' : 'byYear';
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="flex flex-col items-center space-y-6">
          <img src="/assets/loading.gif" alt="Chargement" className="w-40 h-40" />
          <p className="text-2xl text-gray-700 font-semibold text-center">
            Vérification du tableau de bord administrateur…
          </p>
        </div>
      </div>
    );
  }

  const reportData = reportStats ? [
    { name: 'Répondus', value: reportStats.totalReplied },
    { name: 'En attente', value: reportStats.totalPending },
  ] : [];

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="fixed top-0 w-full md:hidden z-10">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-6 pt-20 md:pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {cards.map((card, idx) => (
            <Card key={idx} className={`${card.bg} text-white shadow-lg`}>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-medium">{card.label}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                  <div className="opacity-80">{card.icon}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-md bg-gradient-to-r from-blue-100 to-blue-50">
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Inscriptions Utilisateurs</h3>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={userChartFilter}
                  onChange={(e) => setUserChartFilter(e.target.value)}
                >
                  <option value="day">Jour</option>
                  <option value="month">Mois</option>
                  <option value="year">Année</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userBehaviorData}>
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="shadow-md bg-gradient-to-r from-green-100 to-green-50">
            <CardContent>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Commandes Confirmées</h3>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={orderChartFilter}
                  onChange={(e) => setOrderChartFilter(e.target.value)}
                >
                  <option value="day">Jour</option>
                  <option value="month">Mois</option>
                  <option value="year">Année</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={orderStatsData}>
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-md bg-gradient-to-br from-yellow-100 to-yellow-50">
            <CardContent>
              <h3 className="text-lg font-semibold mb-4">Signalements Utilisateurs</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reportData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#facc15"
                    label
                  >
                    {reportData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="shadow-md bg-gradient-to-br from-pink-100 to-pink-50">
            <CardContent>
              <h3 className="text-lg font-semibold mb-4">Top 7 Produits les Plus Vendus</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topProducts}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#ec4899"
                    label
                  >
                    {topProducts.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;